import { CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { cn } from '../../../lib/utils';
import { datasetLabel, formatDay, overdueLabel, periodLabelFor } from '../uploadCycle';
import type { UploadSummary } from './uploadSummary';

interface UploadSummaryBannerProps {
  summary: UploadSummary;
}

/**
 * Answers the only question a supplier opens this page with: do I owe anything?
 *
 * Red when something is late, amber when something is due but not yet late,
 * green when the supplier is straight with the customer.
 */
export default function UploadSummaryBanner({ summary }: UploadSummaryBannerProps) {
  const { outstandingCount, overdueCount, oldestOutstanding, completeCount, periodCount } = summary;
  const clear = outstandingCount === 0;
  const late = overdueCount > 0;

  const tone = clear
    ? {
        card: 'border-green-200 bg-green-50/60 dark:border-green-900 dark:bg-green-950/30',
        bubble: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
        icon: CheckCircle2,
      }
    : late
      ? {
          card: 'border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/30',
          bubble: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
          icon: AlertTriangle,
        }
      : {
          card: 'border-yellow-200 bg-yellow-50/60 dark:border-yellow-900 dark:bg-yellow-950/30',
          bubble: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
          icon: Clock,
        };
  const Icon = tone.icon;

  const headline = clear
    ? 'You are up to date'
    : `${outstandingCount} file${outstandingCount === 1 ? '' : 's'} still to send`;

  let detail: string;
  if (clear) {
    detail = 'Every file your customer has asked for has been received.';
  } else if (oldestOutstanding) {
    const waiting = overdueLabel(oldestOutstanding.due_date);
    const what = `${datasetLabel(oldestOutstanding)} for ${periodLabelFor(oldestOutstanding)}`;
    detail = waiting
      ? `Longest outstanding: ${what}, due ${formatDay(oldestOutstanding.due_date)} — ${waiting}.`
      : `Next up: ${what}, due ${formatDay(oldestOutstanding.due_date)}.`;
  } else {
    detail = 'Drop each file on its row below.';
  }

  return (
    <Card className={cn(tone.card)} data-testid="upload-summary">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="flex items-start gap-3">
          <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', tone.bubble)}>
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-foreground">{headline}</p>
            <p className="text-sm text-muted-foreground">{detail}</p>
          </div>
        </div>
        {periodCount > 0 && (
          <p className="shrink-0 text-sm text-muted-foreground sm:text-right">
            <span className="font-semibold text-foreground">
              {completeCount} of {periodCount}
            </span>
            {' months complete'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
